import type { ChangeEvent, ReactNode } from 'react'
import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import type { SortDirection } from '@mui/material/TableCell'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Paper from '@mui/material/Paper'
import { visuallyHidden } from '@mui/utils'
import classNames from 'classnames'

import css from './styles.module.css'
import { Collapse, Typography } from '@mui/material'

type EnhancedCell = {
  content: ReactNode
  rawValue: string | number | null
  sticky?: boolean
  mobileLabel?: string
}

type EnhancedRow = {
  selected?: boolean
  collapsed?: boolean
  key?: string
  cells: Record<string, EnhancedCell>
}

type EnhancedHeadCell = {
  id: string
  label: ReactNode
  width?: string
  align?: string
  sticky?: boolean
  disableSort?: boolean
}

function descendingComparator(a: string | number, b: string | number) {
  if (b < a) {
    return -1
  }
  if (b > a) {
    return 1
  }
  return 0
}

function getComparator(order: SortDirection, orderBy: string) {
  return (a: EnhancedRow, b: EnhancedRow) => {
    const aValue = a.cells[orderBy].rawValue
    const bValue = b.cells[orderBy].rawValue

    // Handle null/undefined values - always sort to end
    if (aValue == null) return 1
    if (bValue == null) return -1
    if (aValue == null && bValue == null) return 0

    // Use existing comparator for non-null values
    return order === 'desc' ? descendingComparator(aValue, bValue) : -descendingComparator(aValue, bValue)
  }
}

type EnhancedTableHeadProps = {
  headCells: EnhancedHeadCell[]
  onRequestSort: (property: string) => void
  order: 'asc' | 'desc'
  orderBy: string
}

function EnhancedTableHead(props: EnhancedTableHeadProps) {
  const { headCells, order, orderBy, onRequestSort } = props
  const createSortHandler = (property: string) => () => {
    onRequestSort(property)
  }

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="left"
            padding="normal"
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              width: headCell.width ? headCell.width : '',
              textAlign: headCell.align ? headCell.align : '',
            }}
            className={classNames({ sticky: headCell.sticky })}
          >
            {headCell.disableSort ? (
              <Box component="span" sx={{ fontSize: '14px' }}>
                {headCell.label}
              </Box>
            ) : (
              <>
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                  sx={{
                    mr: headCell.id === 'actions' || headCell.disableSort ? 0 : [0, '-26px'],
                    textWrap: 'nowrap',
                    fontSize: '14px',
                  }}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={{ ...visuallyHidden }}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

export type EnhancedTableProps = {
  rows: EnhancedRow[]
  headCells: EnhancedHeadCell[]
  mobileVariant?: boolean
  compact?: boolean
  footer?: ReactNode
}

const pageSizes = [10, 25, 100]

function EnhancedTable({ rows, headCells, mobileVariant, compact, footer }: EnhancedTableProps) {
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<string>('')
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(pageSizes[1])

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const orderedRows = orderBy ? rows.slice().sort(getComparator(order, orderBy)) : rows
  const pagedRows = orderedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  const showPagination = rows.length > pageSizes[0] || rowsPerPage !== pageSizes[1]

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <TableContainer
        data-testid="table-container"
        component={Paper}
        sx={{
          width: '100%',
          overflowX: ['auto', 'hidden'],
          borderBottomLeftRadius: showPagination ? 0 : '6px',
          borderBottomRightRadius: showPagination ? 0 : '6px',
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          className={classNames({ [css.mobileColumn]: mobileVariant, [css.compactTable]: compact })}
        >
          <EnhancedTableHead headCells={headCells} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
          <TableBody>
            {pagedRows.length > 0 ? (
              pagedRows.map((row, index) => {
                const rowKey = row.key ?? index

                return (
                  <TableRow
                    data-testid="table-row"
                    tabIndex={-1}
                    key={rowKey}
                    selected={row.selected}
                    className={row.collapsed ? css.collapsedRow : undefined}
                  >
                    {Object.entries(row.cells).map(([key, cell]) => (
                      <TableCell
                        key={key}
                        data-testid={`table-cell-${key}`}
                        className={classNames({
                          [css.collapsedCell]: row.collapsed,
                        })}
                      >
                        <Collapse in={!row.collapsed} enter={false}>
                          {cell.mobileLabel ? (
                            <Typography variant="body2" color="text.secondary" className={css.mobileLabel}>
                              {cell.mobileLabel}
                            </Typography>
                          ) : null}

                          {cell.content}
                        </Collapse>
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })
            ) : (
              // Prevent no `tbody` rows hydration error
              <TableRow>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <Box
          component={Paper}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {footer && (
            <Box
              sx={{
                px: 2,
                display: 'flex',
                alignItems: 'center',
                height: '52px',
              }}
            >
              {footer}
            </Box>
          )}
          <TablePagination
            data-testid="table-pagination"
            rowsPerPageOptions={pageSizes}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: 'none',
              height: '52px',
              '& .MuiTablePagination-selectLabel': { color: 'text.secondary', fontSize: '14px' },
              '& .MuiTablePagination-displayedRows': { color: 'primary.light', fontSize: '14px' },
              '& .MuiTablePagination-select': { color: 'primary.light', fontSize: '14px' },
              '& .MuiIconButton-root': { color: 'primary.light' },
            }}
          />
        </Box>
      )}
      {!showPagination && footer && (
        <Box
          component={Paper}
          sx={{
            px: 2,
            display: 'flex',
            alignItems: 'center',
            height: '52px',
            borderTop: '1px solid',
            borderColor: 'var(--color-background-main)',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  )
}

export default EnhancedTable
